import DOMPurify from 'dompurify';

// Whitelists a small set of tags and attributes suitable for chat messages.
const ALLOWED_TAG_NAMES = [
  'b','strong','i','em','s','strike','u','code','pre','blockquote','ul','ol','li','br','a','p','div','span','img'
];

const ALLOWED_TAGS = new Set(ALLOWED_TAG_NAMES);
const ALLOWED_ATTRS = {
  a: new Set(['href','title','target','rel']),
  img: new Set(['src','alt','title','loading','width','height']),
  '*': new Set([]),
};
const ALLOWED_ATTR_NAMES = Array.from(
  new Set(Object.values(ALLOWED_ATTRS).flatMap((attrs) => Array.from(attrs)))
);
const URI_SAFE_ATTR_NAMES = ['alt', 'title', 'target', 'rel', 'loading', 'width', 'height'];
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: ALLOWED_TAG_NAMES,
  ALLOWED_ATTR: ALLOWED_ATTR_NAMES,
  ADD_ATTR: ALLOWED_ATTR_NAMES,
  ADD_URI_SAFE_ATTR: URI_SAFE_ATTR_NAMES,
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?):|\/api\/media\/|data:image\/|blob:)/i,
};

const sanitizeWithDomPurify = (raw) => {
  if (!DOMPurify || typeof DOMPurify.sanitize !== 'function') {
    return null;
  }

  try {
    return DOMPurify.sanitize(raw, DOMPURIFY_CONFIG);
  } catch (_) {
    return null;
  }
};

const parseHtmlBody = (html) => {
  if (typeof DOMParser === 'undefined') {
    return null;
  }

  try {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed && parsed.body ? parsed.body : null;
  } catch (_) {
    return null;
  }
};

const stripTextConservatively = (html) => String(html)
  .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
  .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
  .replace(/<br\s*\/?>(\r?\n)?/gi, '\n')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/gi, ' ');

const isHttpUrl = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const isSafeImageSrc = (src) => {
  if (!src) return false;
  const v = String(src).trim();
  if (v.startsWith('data:image/')) return true;
  if (v.startsWith('blob:')) return true;
  // Allow same-origin media paths routed via the gateway
  if (v.startsWith('/api/media/')) return true;
  return isHttpUrl(v);
};

export function sanitizeHtml(html) {
  const raw = String(html || '');

  // Conservative fallback used when DOM APIs are missing or fail
  const conservative = () => stripTextConservatively(raw);

  const sanitized = sanitizeWithDomPurify(raw);

  let bodyNode = null;
  const htmlToParse = sanitized ?? raw;

  // Try using DOMParser first (may fail or return a document without body on some engines)
  bodyNode = parseHtmlBody(htmlToParse);

  // Last resort: conservative string-based cleanup
  if (!bodyNode) {
    return sanitized ?? conservative();
  }

  if (typeof Node === 'undefined') {
    return sanitized ?? conservative();
  }

  const walk = (node) => {
    if (!node) return;
    // Remove scripts, iframes, style, etc., and clean attributes
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      // Treat root container nodes as always allowed; never strip them
      if (tag !== 'body' && tag !== 'html' && !ALLOWED_TAGS.has(tag)) {
        // Replace unknown element with its text content
        const parent = node.parentNode;
        if (parent) {
          while (node.firstChild) parent.insertBefore(node.firstChild, node);
          parent.removeChild(node);
        }
        return;
      }
      // Clean attributes
      const allowed = new Set([...(ALLOWED_ATTRS['*'] || []), ...((ALLOWED_ATTRS[tag]) || [])]);
      // copy list as NamedNodeMap is live
      Array.from(node.attributes || []).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (!allowed.has(name)) {
          node.removeAttribute(attr.name);
          return;
        }
        if (tag === 'a' && name === 'href') {
          const value = (attr.value || '').trim();
          if (!isHttpUrl(value)) {
            node.removeAttribute('href');
          } else {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
          }
        } else if (tag === 'img' && name === 'src') {
          const value = (attr.value || '').trim();
          if (!isSafeImageSrc(value)) {
            node.removeAttribute('src');
          } else if (!node.getAttribute('loading')) {
            // Enforce lazy loading by default
            node.setAttribute('loading', 'lazy');
          }
        } else if (tag === 'img' && (name === 'width' || name === 'height')) {
          const n = parseInt(attr.value, 10);
          if (!Number.isFinite(n) || n <= 0 || n > 5000) {
            node.removeAttribute(name);
          }
        }
      });
    }
    // Recurse children
    let child = node.firstChild;
    while (child) {
      const next = child.nextSibling;
      walk(child);
      child = next;
    }
  };

  walk(bodyNode);
  return bodyNode.innerHTML;
}

export function stripHtml(html) {
  if (!html) {
    return '';
  }

  const raw = String(html);
  const sanitized = sanitizeWithDomPurify(raw);
  const bodyNode = parseHtmlBody(sanitized ?? raw);

  if (bodyNode) {
    return (bodyNode.textContent || '').replace(/\u00A0/g, ' ');
  }

  // Server-side fallback without a DOM implementation
  return stripTextConservatively(raw);
}

export function hasVisibleContent(html) {
  if (!html) {
    return false;
  }
  const plain = stripHtml(html);
  if (plain && plain.trim().length > 0) {
    return true;
  }
  // Check for non-textual inline nodes that might still represent content (e.g. <img>, <video>)
  return /<\s*(img|video|audio|canvas)\b/i.test(html);
}
